/**
 * Extended ThemeProvider — base theme from @flowstate/ui plus app-specific
 * background, performance, and holographic preferences.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import {
  ThemeProvider as BaseThemeProvider,
  useTheme as useBaseTheme,
  GLOW_MULTIPLIERS,
} from "@flowstate/ui";
export { GLOW_MULTIPLIERS };
export type { Theme, ColorTheme, GlowIntensity } from "@flowstate/ui";

export type BackgroundType = "logo" | "image" | "webgl" | "mycelium" | "slideshow" | "none";
export type HolographicIntensity = "subtle" | "medium" | "intense";

const PERF_KEY = "flowstate-ui-performance-mode";
const BG_TYPE_KEY = "flowstate-ui-background-type";
const BG_IMAGES_KEY = "flowstate-ui-background-images";
const HOLO_KEY = "flowstate-ui-holographic-mode";
const HOLO_INTENSITY_KEY = "flowstate-ui-holographic-intensity";
const GLASS_OPACITY_KEY = "flowstate-ui-glass-opacity";

type ExtendedThemeState = {
  performanceMode: boolean;
  setPerformanceMode: (value: boolean) => void;
  backgroundType: BackgroundType;
  setBackgroundType: (value: BackgroundType) => void;
  backgroundImages: string[];
  setBackgroundImages: (value: string[]) => void;
  holographicMode: boolean;
  setHolographicMode: (value: boolean) => void;
  holographicIntensity: HolographicIntensity;
  setHolographicIntensity: (value: HolographicIntensity) => void;
  glassOpacity: number;
  setGlassOpacity: (value: number) => void;
};

const ExtendedThemeContext = createContext<ExtendedThemeState | null>(null);

const VALID_BACKGROUND_TYPES: BackgroundType[] = [
  "logo",
  "image",
  "webgl",
  "mycelium",
  "slideshow",
  "none",
];
const VALID_HOLO_INTENSITIES: HolographicIntensity[] = [
  "subtle",
  "medium",
  "intense",
];
const GLASS_OPACITY_MIN = 0.2;
const GLASS_OPACITY_MAX = 0.95;

const clampGlassOpacity = (v: number) =>
  Math.max(GLASS_OPACITY_MIN, Math.min(GLASS_OPACITY_MAX, v));

function readJson<T>(
  key: string,
  fallback: T,
  validate?: (value: unknown) => boolean
): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (validate && !validate(parsed)) return fallback;
    return parsed as T;
  } catch {
    return fallback;
  }
}

const isBoolean = (v: unknown) => typeof v === "boolean";
const isStringArray = (v: unknown) =>
  Array.isArray(v) && v.every((i) => typeof i === "string");

function ExtendedThemeProvider({ children }: { children: ReactNode }) {
  const [performanceMode, setPerformanceModeState] = useState(() =>
    readJson(PERF_KEY, false, isBoolean)
  );
  const [backgroundType, setBackgroundTypeState] = useState<BackgroundType>(() =>
    readJson<BackgroundType>(BG_TYPE_KEY, "logo", (v) =>
      VALID_BACKGROUND_TYPES.includes(v as BackgroundType)
    )
  );
  const [backgroundImages, setBackgroundImagesState] = useState<string[]>(() =>
    readJson<string[]>(BG_IMAGES_KEY, [], isStringArray)
  );
  const [holographicMode, setHolographicModeState] = useState(() =>
    readJson(HOLO_KEY, true, isBoolean)
  );
  const [holographicIntensity, setHolographicIntensityState] =
    useState<HolographicIntensity>(() =>
      readJson<HolographicIntensity>(HOLO_INTENSITY_KEY, "medium", (v) =>
        VALID_HOLO_INTENSITIES.includes(v as HolographicIntensity)
      )
    );
  const [glassOpacity, setGlassOpacityState] = useState<number>(() =>
    clampGlassOpacity(
      readJson<number>(
        GLASS_OPACITY_KEY,
        0.65,
        (v) => typeof v === "number" && Number.isFinite(v)
      )
    )
  );

  const setPerformanceMode = useCallback((value: boolean) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(PERF_KEY, JSON.stringify(value));
    }
    setPerformanceModeState(value);
  }, []);

  const setBackgroundType = useCallback((value: BackgroundType) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(BG_TYPE_KEY, JSON.stringify(value));
    }
    setBackgroundTypeState(value);
  }, []);

  const setBackgroundImages = useCallback((value: string[]) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(BG_IMAGES_KEY, JSON.stringify(value));
    }
    setBackgroundImagesState(value);
  }, []);

  const setHolographicMode = useCallback((value: boolean) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(HOLO_KEY, JSON.stringify(value));
    }
    setHolographicModeState(value);
  }, []);

  const setHolographicIntensity = useCallback((value: HolographicIntensity) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(HOLO_INTENSITY_KEY, JSON.stringify(value));
    }
    setHolographicIntensityState(value);
  }, []);

  const setGlassOpacity = useCallback((value: number) => {
    const clamped = clampGlassOpacity(value);
    if (typeof window !== "undefined") {
      localStorage.setItem(GLASS_OPACITY_KEY, JSON.stringify(clamped));
    }
    setGlassOpacityState(clamped);
  }, []);

  // Cross-tab sync for extended keys (parity with the base provider, which
  // syncs theme/colorTheme/glowIntensity the same way)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorage = (e: StorageEvent) => {
      switch (e.key) {
        case PERF_KEY:
          setPerformanceModeState(readJson(PERF_KEY, false, isBoolean));
          break;
        case BG_TYPE_KEY:
          setBackgroundTypeState(
            readJson<BackgroundType>(BG_TYPE_KEY, "logo", (v) =>
              VALID_BACKGROUND_TYPES.includes(v as BackgroundType)
            )
          );
          break;
        case BG_IMAGES_KEY:
          setBackgroundImagesState(
            readJson<string[]>(BG_IMAGES_KEY, [], isStringArray)
          );
          break;
        case HOLO_KEY:
          setHolographicModeState(readJson(HOLO_KEY, true, isBoolean));
          break;
        case HOLO_INTENSITY_KEY:
          setHolographicIntensityState(
            readJson<HolographicIntensity>(HOLO_INTENSITY_KEY, "medium", (v) =>
              VALID_HOLO_INTENSITIES.includes(v as HolographicIntensity)
            )
          );
          break;
        case GLASS_OPACITY_KEY:
          setGlassOpacityState(
            clampGlassOpacity(
              readJson<number>(
                GLASS_OPACITY_KEY,
                0.65,
                (v) => typeof v === "number" && Number.isFinite(v)
              )
            )
          );
          break;
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Apply performance mode class to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (performanceMode) {
      root.classList.add("performance-mode");
    } else {
      root.classList.remove("performance-mode");
    }
  }, [performanceMode]);

  // Apply holographic mode + intensity classes & CSS variables to <html>
  useEffect(() => {
    const root = document.documentElement;
    const intensityClasses: HolographicIntensity[] = ["subtle", "medium", "intense"];
    root.style.setProperty("--holo-card-alpha", String(glassOpacity));
    root.style.setProperty("--card-opacity", String(glassOpacity));

    if (holographicMode && !performanceMode) {
      root.classList.add("holographic-mode");
      intensityClasses.forEach((level) => root.classList.remove(`holographic-${level}`));
      root.classList.add(`holographic-${holographicIntensity}`);
    } else {
      root.classList.remove("holographic-mode");
      intensityClasses.forEach((level) => root.classList.remove(`holographic-${level}`));
    }
  }, [holographicMode, holographicIntensity, performanceMode, glassOpacity]);

  return (
    <ExtendedThemeContext.Provider
      value={{
        performanceMode,
        setPerformanceMode,
        backgroundType,
        setBackgroundType,
        backgroundImages,
        setBackgroundImages,
        holographicMode,
        setHolographicMode,
        holographicIntensity,
        setHolographicIntensity,
        glassOpacity,
        setGlassOpacity,
      }}
    >
      {children}
    </ExtendedThemeContext.Provider>
  );
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
}: {
  children: ReactNode;
  defaultTheme?: import("@flowstate/ui").Theme;
}) {
  return (
    <BaseThemeProvider defaultTheme={defaultTheme}>
      <ExtendedThemeProvider>{children}</ExtendedThemeProvider>
    </BaseThemeProvider>
  );
}

export function useTheme() {
  const base = useBaseTheme();
  const extended = useContext(ExtendedThemeContext);
  if (extended === null) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return { ...base, ...extended };
}
